/**
 * Client for the kip-backend `/pwa/api/storage/*` proxy — the coop's files on
 * Dropbox, brokered per account by the backend. Every path here is
 * coop-relative and forward-slashed (`nest/concepts/x.md`, `clucks/2026-08.md`);
 * the backend keeps the phone inside `pages/`, `nest/`, `clucks/`, `exports/`.
 */
import { api } from './api';
import type { BatchDownloadResponse, DeltaResponse, DownloadedFile } from '../shared/api-types.js';

/** Incremental listing. No cursor → a full walk (`full: true`); then pass `cursor`. */
export function storageDelta(cursor?: string): Promise<DeltaResponse> {
  return api.post<DeltaResponse>('/pwa/api/storage/delta', cursor ? { cursor } : {});
}

/** Fetch many files at once. Paths that 404 come back in `missing`. */
export function storageBatchDownload(paths: string[]): Promise<BatchDownloadResponse> {
  return api.post<BatchDownloadResponse>('/pwa/api/storage/batch-download', { paths });
}

/** Fetch one file. */
export function storageDownload(path: string): Promise<DownloadedFile> {
  return api.get<DownloadedFile>(`/pwa/api/storage/download?path=${encodeURIComponent(path)}`);
}

/** Direct (cookie-authenticated) URL to download an export file — use as an
 *  `<a href>` so the browser downloads it with the right content-type. */
export function storageExportUrl(path: string): string {
  return `/pwa/api/storage/export?path=${encodeURIComponent(path)}`;
}

export interface CoopList {
  current: string;
  coops: string[];
}

/** Folders at the App-folder root — candidates for which one is the coop. */
export function storageCoops(): Promise<CoopList> {
  return api.get<CoopList>('/pwa/api/storage/coops');
}

/** Pick which App-folder subfolder is this account's coop (`''` = the root). */
export function storageSetCoop(path: string): Promise<{ ok: boolean; coopPath: string }> {
  return api.post<{ ok: boolean; coopPath: string }>('/pwa/api/storage/coop', { path });
}

/** Write a capture into `pages/`. The backend autorenames on collision and
 *  returns the final coop-relative path. `clientId` dedupes retries. */
export function storageEgg(
  slug: string,
  markdown: string,
  clientId: string,
): Promise<{ path: string }> {
  return api.post<{ path: string }>('/pwa/api/storage/egg', { slug, markdown, clientId });
}
